import React from 'react';
import Icon from '../AppIcon';

const WorkflowProgressIndicator = ({ 
  currentStep = 0, 
  completedSteps = [], 
  processingStep = null,
  className = '' 
}) => {
  const steps = [
    { 
      id: 'upload', 
      label: 'Upload', 
      icon: 'Upload',
      path: '/file-upload-dashboard'
    },
    { 
      id: 'configure', 
      label: 'Configure', 
      icon: 'Settings',
      path: '/column-mapping-interface'
    },
    { 
      id: 'process', 
      label: 'Process', 
      icon: 'Activity',
      path: '/processing-status-monitor'
    },
    { 
      id: 'analyze', 
      label: 'Analyze', 
      icon: 'BarChart3',
      path: '/results-dashboard'
    },
    { 
      id: 'review', 
      label: 'Review', 
      icon: 'Eye',
      path: '/manual-review-interface'
    },
    { 
      id: 'export', 
      label: 'Export', 
      icon: 'Download',
      path: '/export-and-reports-center'
    }
  ];

  const getStepStatus = (stepIndex, stepId) => {
    if (completedSteps?.includes(stepId)) return 'completed';
    if (processingStep === stepId) return 'processing';
    if (stepIndex === currentStep) return 'current';
    if (stepIndex < currentStep) return 'completed';
    return 'pending';
  };

  const getStepStyles = (status) => {
    switch (status) {
      case 'completed':
        return {
          container: 'bg-success text-success-foreground',
          connector: 'bg-success'
        };
      case 'processing':
        return {
          container: 'bg-warning text-warning-foreground animate-pulse',
          connector: 'bg-warning'
        };
      case 'current':
        return {
          container: 'bg-primary text-primary-foreground ring-2 ring-primary ring-opacity-20',
          connector: 'bg-border'
        };
      default:
        return {
          container: 'bg-muted text-muted-foreground',
          connector: 'bg-border'
        };
    }
  };

  const handleStepClick = (step, stepIndex, status) => {
    if (status === 'completed' || status === 'current') {
      window.location.href = step?.path;
    }
  };

  return (
    <div className={`flex items-center justify-center space-x-4 ${className}`}>
      {steps?.map((step, index) => {
        const status = getStepStatus(index, step?.id);
        const styles = getStepStyles(status);
        const isClickable = status === 'completed' || status === 'current';

        return (
          <div key={step?.id} className="flex items-center">
            {/* Step Circle */}
            <button
              onClick={() => handleStepClick(step, index, status)}
              disabled={!isClickable}
              className={`
                relative flex items-center justify-center w-10 h-10 rounded-full transition-all duration-200 ease-smooth
                ${styles?.container}
                ${isClickable ? 'cursor-pointer hover:scale-105' : 'cursor-not-allowed'}
                ${status === 'processing' ? 'shadow-lg' : ''}
              `}
              title={`${step?.label} - ${status}`}
            >
              {status === 'completed' ? (
                <Icon name="Check" size={16} />
              ) : status === 'processing' ? (
                <Icon name="Loader2" size={16} className="animate-spin" />
              ) : (
                <Icon name={step?.icon} size={16} />
              )}
            </button>
            {/* Step Label - Hidden on mobile */}
            <span className={`
              hidden sm:block ml-2 text-xs font-medium
              ${status === 'current' ? 'text-foreground' : 'text-muted-foreground'}
            `}>
              {step?.label}
            </span>
            {/* Connector Line */}
            {index < steps?.length - 1 && (
              <div className={`
                hidden md:block w-8 h-0.5 mx-4 transition-colors duration-200
                ${styles?.connector}
              `} />
            )}
          </div>
        );
      })}
    </div>
  );
};

export default WorkflowProgressIndicator;